import cn from 'classnames';
import { FC, useEffect, useRef } from 'react';
import { useParams } from 'react-router';

import { MedicalImageInterface } from '@/shared/config';
import { ViewMIR } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import {
  Column,
  Layout,
  MobileLayout,
  Skeleton,
  WidgetCard,
  WidgetLayout,
} from '@/shared/ui/';

import { assetsModel } from '@/entities/assets';
import { conditionModel, useToothConditions } from '@/entities/condition';
import { ConfirmModal } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';
import { useIsPseudoFullscreen } from '@/entities/toolbarMIR';
import {
  getDisplayToothNumber,
  isThirdMolarTooth,
  toothModel,
} from '@/entities/tooth';

import {
  medicalImageModel,
  useAddAndRemoveMedicalImage,
} from '@/features/medicalImage';
import { MedicalImageToolbar } from '@/features/medicalImageToolbar';
import { MaskFiltersWrapper, MaskTooltip } from '@/features/renderMasks';
import { SimplifiedToothChart } from '@/features/simplifiedToothChart';

import { AddConditionModal } from '@/widgets/AddConditionModal';
import {
  FullHeadMprSlicesProvider,
  useFullHeadMprSlicesContext,
} from '@/widgets/FullHeadMPR';
import { GetFullAccessModal } from '@/widgets/GetFullAccessModal';
import { MobileHeader } from '@/widgets/Header';
import { MIRenderModal } from '@/widgets/MIRenderModal';
import {
  ApproveToothButton,
  ToothCard,
  ToothCardFDA,
} from '@/widgets/ToothCard';
import { UploadedSlices } from '@/widgets/UploadedSlices';

import { ToothNumberEditPopup } from '@/pages/GpReport/ui/ToothNumberEditPopup';

import { PanoReformatRender } from '../GpReport/ui/PanoReformatRender/PanoReformatRender';

import { SKELETON_DEFAULT_HEIGHT } from './config';
import styles from './Tooth.module.scss';
import {
  ToothCardFullHeadMprSlices,
  ToothMedicalImagesGroups,
  ToothMPR,
} from './ui';

type ToothProps = {
  className?: string;
};

type ToothCardWithFullHeadMprSlicesProps = {
  selectedMedicalImages: MedicalImageInterface[];
  toothID: string;
  className?: string;
  showViewerButton?: boolean;
};

const ToothCardWithFullHeadMprSlices: FC<
  ToothCardWithFullHeadMprSlicesProps
> = (props) => {
  const { selectedMedicalImages, toothID, className, showViewerButton } = props;

  const selectedUploads = useAppSelector(
    medicalImageModel.selectors.selectSelectedUploadedMedicalImages(toothID),
  );

  const {
    branch,
    clearSelection,
    isClearingSelection,
    isSelectionReady,
    pendingTileIDs,
    selectedTiles,
  } = useFullHeadMprSlicesContext();

  return (
    <ToothCard
      className={className}
      toothID={toothID}
      imageContainerComponent={
        <ToothCardFullHeadMprSlices
          selectedMedicalImages={selectedMedicalImages}
          toothID={toothID}
        />
      }
      removeAllSlicesControl={
        branch === 'server'
          ? undefined
          : {
              hasSelectedSlices:
                selectedTiles.length > 0 || selectedUploads.length > 0,
              isLoading: isClearingSelection || pendingTileIDs.length > 0,
              isDisabled: !isSelectionReady,
              onRemove: clearSelection,
            }
      }
      showRemoveAllSlices
      showViewerButton={showViewerButton}
    />
  );
};

export const Tooth: FC<ToothProps> = (props) => {
  const { className } = props;

  const { reportID = '', patientID = '', toothID = '' } = useParams();
  const { isMobile } = useMedia();
  const isPseudoFullscreen = useIsPseudoFullscreen();

  const widgetRef = useRef<HTMLDivElement>(null);

  const report = useAppSelector(reportsModel.selectors.selectByID(reportID));
  const tooth = useAppSelector((state) =>
    toothModel.selectors.selectById(state, toothID),
  );
  const { isFDA } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );
  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );
  const conditionLoading = useAppSelector(
    conditionModel.selectors.selectLoading,
  );
  const panoImage = useAppSelector(
    assetsModel.selectors.selectCBCTGPPanoImageByReportID(reportID),
  );
  const selectedMedicalImages = useAppSelector(
    medicalImageModel.selectors.selectActiveMedicalImages(toothID),
  );
  const toothMedicalImageGroups = useAppSelector(
    medicalImageModel.selectors.selectMedicalImageGroups(toothID),
  );

  const { toggleMedicalImageSelection } = useAddAndRemoveMedicalImage();

  const reportType = report?.Type;
  const ISONumber = tooth?.Numeration?.ISO;

  const isConditionsLoaded = conditionLoading === 'succeeded';
  const isToothLoaded = !!tooth;
  const isToothApproved = tooth?.IsApproved ?? false;

  const displayedToothNumber = getDisplayToothNumber(
    ISONumber ?? 0,
    dentalNotationFormat,
  );

  // TODO: Make a selector just with checking missing condition code by tooth and return boolean.
  const { isMissingTooth } = useToothConditions({
    toothID: tooth?.ID ?? '',
  });

  const isMissingThirdMolar =
    isThirdMolarTooth(tooth?.Numeration?.ISO ?? 0) && isMissingTooth;

  const toggleSelected = (medicalImage: MedicalImageInterface) => {
    toggleMedicalImageSelection({
      toothID,
      medicalImage,
      selectedMedicalImages,
    });
  };

  useEffect(() => {
    if (reportType && ISONumber) {
      trackEvent({
        eventName: 'Tooth Page Viewed',
        properties: {
          patient_id: patientID,
          report_id: reportID,
          tooth_number: ISONumber,
          report_type: reportType,
        },
        isPageViewEvent: true,
      });
    }
  }, [reportType, ISONumber]);

  if (isMobile) {
    return (
      <FullHeadMprSlicesProvider
        patientID={patientID}
        reportID={reportID}
        toothID={toothID}
      >
        <MobileLayout>
          <MobileHeader showBackButton />
          <MobileLayout.Main>
            {isToothLoaded ? (
              <ToothCardWithFullHeadMprSlices
                selectedMedicalImages={selectedMedicalImages}
                toothID={tooth.ID}
                showViewerButton={false}
              />
            ) : (
              <Skeleton width="100%" borderRadius="8px" height="46px" />
            )}

            {!panoImage ? (
              <Skeleton
                width="100%"
                borderRadius="8px"
                height={`${SKELETON_DEFAULT_HEIGHT}px`}
              />
            ) : (
              <PanoReformatRender>
                <MedicalImageToolbar
                  toolbarID="main"
                  className={cn(styles.mobileToolbar)}
                />

                <MaskFiltersWrapper className={styles.maskFilters} />

                <MaskTooltip />
              </PanoReformatRender>
            )}

            {!isMissingThirdMolar && (
              <ToothMedicalImagesGroups
                toothID={toothID}
                selectedMedicalImages={selectedMedicalImages}
                toggleSelected={toggleSelected}
                groups={toothMedicalImageGroups}
              />
            )}

            <UploadedSlices toothID={toothID} toggleSelected={toggleSelected} />
          </MobileLayout.Main>
          <MobileLayout.Footer>
            <div className={styles.footerWrapper}>
              <ApproveToothButton
                toothID={toothID}
                isApproved={isToothApproved}
                disabled={!report?.YourPermissions?.CanChangeToothApproved}
                className={styles.approveButton}
              />
            </div>
          </MobileLayout.Footer>
        </MobileLayout>

        {/* Modals */}
        <ToothNumberEditPopup />
        <MIRenderModal />
        <GetFullAccessModal />
        <AddConditionModal />
        <ConfirmModal />
      </FullHeadMprSlicesProvider>
    );
  }

  return (
    <FullHeadMprSlicesProvider
      patientID={patientID}
      reportID={reportID}
      toothID={toothID}
    >
      <Layout.Content>
        <Layout.Main>
          <Layout.Content
            className={cn(styles.container, className)}
            data-section="tooth-analysis"
          >
            <WidgetLayout className={styles.columnWrapper}>
              <Column className={styles.leftColumnWrapper}>
                <div className={cn(styles.leftFixedColumn)}>
                  {isToothLoaded ? (
                    isFDA ? (
                      <ToothCardFDA
                        className={cn(styles.mb16, styles.toothCard)}
                        id={tooth.ID}
                        patientID={patientID ?? ''}
                        reportID={reportID ?? ''}
                        showViewerButton={false}
                        // TODO: [8|m] Temporary fast solution for demo. Should be refactored
                        imageContainerComponent={
                          <ToothCardFullHeadMprSlices
                            selectedMedicalImages={selectedMedicalImages}
                            toothID={tooth?.ID ?? ''}
                          />
                        }
                      />
                    ) : (
                      <ToothCardWithFullHeadMprSlices
                        className={cn(styles.mb16, styles.toothCard)}
                        selectedMedicalImages={selectedMedicalImages}
                        toothID={tooth.ID}
                        showViewerButton={false}
                      />
                    )
                  ) : (
                    <Skeleton width="100%" borderRadius="8px" height="46px" />
                  )}
                  <div ref={widgetRef}>
                    <WidgetCard className={cn(styles.panoImageWidget)}>
                      <ToothNumberEditPopup />

                      {!panoImage ? (
                        <Skeleton
                          width="100%"
                          borderRadius="8px"
                          height={`${SKELETON_DEFAULT_HEIGHT}px`}
                        />
                      ) : (
                        <div
                          data-component="tooth-medical-image-view"
                          style={{ display: 'contents' }}
                        >
                          <ViewMIR
                            viewName="main"
                            id="report_render"
                            className={cn(
                              styles.viewMIR,
                              isPseudoFullscreen && styles.pseudoFullscreen,
                            )}
                          >
                            <MedicalImageToolbar
                              toolbarID="main"
                              className={cn(styles.toolbar, styles.toolbarIn)}
                            />
                            <MaskFiltersWrapper
                              className={styles.maskFilters}
                            />
                            <MaskTooltip />
                          </ViewMIR>
                        </div>
                      )}
                    </WidgetCard>
                  </div>
                </div>
              </Column>

              {isConditionsLoaded && (
                <Column className={cn(styles.rightColumn)}>
                  <WidgetCard>
                    <ToothMPR
                      isMissingThirdMolar={isMissingThirdMolar}
                      patientID={patientID}
                      reportID={reportID}
                      toothID={toothID}
                    />
                  </WidgetCard>

                  {!isMissingThirdMolar && (
                    <ToothMedicalImagesGroups
                      toothID={toothID}
                      selectedMedicalImages={selectedMedicalImages}
                      toggleSelected={toggleSelected}
                      groups={toothMedicalImageGroups}
                    />
                  )}

                  <UploadedSlices
                    toothID={toothID}
                    toggleSelected={toggleSelected}
                  />
                </Column>
              )}
            </WidgetLayout>
          </Layout.Content>
        </Layout.Main>
      </Layout.Content>

      <Layout.Footer settingsFooter>
        <div className={styles.footerWrapper}>
          {/* TODO: [8|h] refactore to base ToothChart ui components */}
          <SimplifiedToothChart
            reportID={reportID ?? ''}
            patientID={patientID ?? ''}
            toothOfInterestNumber={displayedToothNumber as number}
            isToothPage
          />

          {!isFDA && (
            <div className={styles.footerButtons}>
              <ApproveToothButton
                toothID={toothID}
                isApproved={isToothApproved}
                disabled={!report?.YourPermissions?.CanChangeToothApproved}
              />
            </div>
          )}
        </div>
      </Layout.Footer>

      <MIRenderModal />
      <GetFullAccessModal />
      <AddConditionModal />
      <ConfirmModal />
    </FullHeadMprSlicesProvider>
  );
};
